import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { CurrentMember, CURRENT_MEMBER_FRAGMENT } from "../screens/fragments";

export const CURRENT_MEMBER_SETTINGS = gql`
  query currentMemberSettings {
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

export const SETTINGS_MUTATION = gql`
  mutation createMemberSettings($input: MembershipCreateMemberSettingsInput!) {
    createMemberSettings(input: $input) {
      member {
        ...CurrentMemberFragment
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface Query {
  currentMember: CurrentMember;
}

const isoNow = () => new Date().toISOString();

// default all initial values to true since it's an async call to get the cached data
const initialValues = {
  activityReviewOnboarding: true,
  mobileAppOnboarding: true,
  deferredCompOnboarding: true,
  planThresholdMet: true,
  acknowledgedPlanChangeAt: isoNow(),
};

type INITIAL_VALUES = typeof initialValues;
type FIELDS = keyof INITIAL_VALUES;

export function useSettings() {
  const [updateSettings] = useMutation(SETTINGS_MUTATION);
  const [settings, setSettings] = useState(initialValues);

  useQuery<Query>(CURRENT_MEMBER_SETTINGS, {
    fetchPolicy: "cache-first",
    onCompleted(data) {
      if (data?.currentMember.memberSettings) {
        setSettings(data.currentMember.memberSettings);
      }
    },
  });

  function optimisticallyChange<FIELD extends FIELDS>(field: FIELD, getValue: () => INITIAL_VALUES[FIELD]) {
    return useCallback(() => {
      const value = getValue();
      setSettings((settings) => ({ ...settings, [field]: value }));
      updateSettings({ variables: { input: { [field]: value } } }).catch(() => {
        setSettings((settings) => ({ ...settings, [field]: null }));
      });
    }, []);
  }

  const setActivityReviewOnboarding = optimisticallyChange("activityReviewOnboarding", () => true);
  const setMobileAppOnboarding = optimisticallyChange("mobileAppOnboarding", () => true);
  const setDeferredCompOnboarding = optimisticallyChange("deferredCompOnboarding", () => true);
  const setPlanThresholdMet = optimisticallyChange("planThresholdMet", () => true);
  const setAcknowledgedPlanChange = optimisticallyChange("acknowledgedPlanChangeAt", isoNow);

  return {
    ...settings,
    acknowledgedPlanChangeAt: settings.acknowledgedPlanChangeAt ? new Date(settings.acknowledgedPlanChangeAt) : undefined,
    setActivityReviewOnboarding,
    setMobileAppOnboarding,
    setDeferredCompOnboarding,
    setPlanThresholdMet,
    setAcknowledgedPlanChange,
  };
}
